import { useCallback, useMemo } from 'react';
import { translate } from '../i18n/translations';
import { formatMoney, formatWithDatePattern, getIntlLocale } from '../utils/localeFormat';
import { LOCALE_DEFAULTS } from '../i18n/localeConfig';

interface InvoiceLocaleFields {
  localeLanguage?: string;
  localeCurrency?: string;
  localeTimezone?: string;
  localeDateFormat?: string;
}

export function useInvoiceLocale(invoice?: InvoiceLocaleFields | null) {
  const language = invoice?.localeLanguage || LOCALE_DEFAULTS.language;
  const currency = invoice?.localeCurrency || LOCALE_DEFAULTS.currency;
  const timezone = invoice?.localeTimezone || LOCALE_DEFAULTS.timezone;
  const dateFormat = invoice?.localeDateFormat || LOCALE_DEFAULTS.dateFormat;

  const t = useCallback((key: string) => translate(language, key), [language]);

  const formatCurrency = useCallback(
    (amount: number) => formatMoney(amount, currency, language, { maximumFractionDigits: 2 }),
    [currency, language]
  );

  const formatDate = useCallback(
    (date: Date | string, options?: Intl.DateTimeFormatOptions) => {
      const d = typeof date === 'string' ? new Date(date) : date;
      if (
        options?.hour ||
        options?.minute ||
        options?.second ||
        options?.dateStyle ||
        options?.timeStyle
      ) {
        return new Intl.DateTimeFormat(getIntlLocale(language, currency), {
          timeZone: timezone,
          ...options,
        }).format(d);
      }
      return formatWithDatePattern(d, dateFormat, language, timezone, options);
    },
    [language, currency, timezone, dateFormat]
  );

  return useMemo(
    () => ({ language, currency, timezone, dateFormat, t, formatCurrency, formatDate }),
    [language, currency, timezone, dateFormat, t, formatCurrency, formatDate]
  );
}
